<script lang="ts">
	import Field from './field.svelte'

	interface Props {
		type?:
			| 'text'
			| 'textarea'
			| 'file'
			| 'select'
			| 'datalist'
			| 'password'
			| 'email'
			| 'date'
			| 'search'
			| 'number'
			| 'time'
			| 'url'
			| 'tel'
			| 'datetime-local'
			| 'month'
			| 'week'
			| 'hidden'
		name?: string
		id?: string
		placeholder?: string
		required?: boolean
		options?:
			| Record<string, string>
			| Array<string>
			| Array<{ label: string; value: string; disabled: boolean; selected: boolean }>
		icon?: string
		invisible?: boolean
		label?: string
		inline?: boolean
		instruction?: string
		optional?: boolean
		multiple?: boolean
		value?: string
		rows?: number
		onchange?: (event: Event) => void
	}

	let {
		type = 'text',
		name,
		id,
		placeholder = undefined,
		required = undefined,
		options = undefined,
		icon = undefined,
		invisible = false,
		label = undefined,
		inline = undefined,
		instruction = undefined,
		optional = false,
		multiple = undefined,
		value = $bindable(),
		rows = 3,
		onchange = () => void 0
	}: Props = $props()

	// TODO: work out click zone of select (invisible select)
</script>

<div class="field">
	{#if label && !inline}
		<label class="label" for={id} data-required={required}
			>{label}{#if optional}<sup class="optional">(optional)</sup>{/if}</label
		>
	{/if}
	<div class="inner" class:prefix={icon} class:invisible>
		{#if icon}
			<svg class="icon prefix">
				<use xlink:href="/images/vector.svg#{icon}"></use>
			</svg>
		{/if}
		{#if label && inline}
			<label class="inlabel" for={id}>{label}</label>
		{/if}
		{#if options}
			{#if type === 'datalist' && Array.isArray(options)}
				<input class="input datalist" list={id} {name} {placeholder} {required} bind:value {onchange} />
				<datalist {id}>
					{#each options as value (value)}
						<option {value}></option>
					{/each}
				</datalist>
			{:else if multiple}
				<select class="select" {id} {name} {required} bind:value multiple {onchange}>
					{#if placeholder}
						<option disabled selected value="">{placeholder}</option>
					{/if}
					{#each Object.entries(options) as [value, label] (value)}
						<option {value}>{label}</option>
					{/each}
				</select>
			{:else}
				<select class="select" {id} {name} {required} bind:value {onchange}>
					{#if placeholder}
						<option disabled={required} selected value="">{placeholder}</option>
					{/if}
					{#if Array.isArray(options)}
						{#each options as option (option)}
							{#if typeof option === 'string'}
								<option value={option}>{option}</option>
							{:else}
								{@const { value, label, disabled, selected } = option}
								<option {value} {disabled} {selected}>{label}</option>
							{/if}
						{/each}
					{:else}
						{#each Object.entries(options) as [value, label] (value)}
							<option {value}>{label}</option>
						{/each}
					{/if}
				</select>
			{/if}
			<svg class="icon alt">
				<use xlink:href="/images/vector.svg#chevron-up"></use>
			</svg>
		{:else if type === 'textarea'}
			<textarea class="textarea" {id} {name} {placeholder} {required} {rows} bind:value {onchange}></textarea>
		{:else if type === 'file'}
			<input class="input" {type} {id} {name} {placeholder} {required} />
		{:else}
			<input
				class="input"
				class:prefix={icon}
				{type}
				{id}
				{name}
				{placeholder}
				{required}
				bind:value
				{onchange}
			/>
		{/if}
	</div>
	{#if instruction}
		<div class="instruction">{instruction}</div>
	{/if}
</div>

{#if type === 'select' && value === 'Other (please specify)'}
	<Field type="textarea" name="{name}->Other" required placeholder="Please specify" />
{/if}

<style lang="css">
	.field {
		font: 400 var(--f-body2);
		letter-spacing: var(--ls-body2);
	}

	.inner {
		background-color: var(--c-white);
		border-radius: var(--r-xxs);
		color: var(--c-foreground, var(--c-crocus));
		display: flex;
		align-items: center;
		gap: 0.875rem;
		min-height: 3.5rem;
		padding-inline: 1rem 0;
		/* there all along, just clear, so focusing fades it in */
		outline: 1.5px solid transparent;
		transition: outline-color 0.2s ease;

		&.prefix {
			padding-inline-start: 3.5rem;
		}

		&:focus-within {
			outline-color: currentColor;
		}

		&.invisible {
			background-color: transparent;
			color: var(--c-charcoal);
			padding-inline: 0;
		}
	}

	.label {
		color: var(--c-foreground);
		display: block;
		font: 500 var(--f-body2);
		letter-spacing: var(--ls-body2);
		margin-block-end: 0.5rem;
	}

	.inlabel {
		font: 400 var(--f-body1);
		color: var(--c-charcoal);
		margin-inline-end: -0.5rem;
	}

	.select,
	.input,
	.textarea {
		appearance: none;
		color: var(--c-charcoal);
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		padding: 0.875rem 0;
		width: 100%;

		&::placeholder {
			color: var(--c-charcoal-placeholder);
		}

		&:focus {
			outline: none;
		}
	}

	.input {
		&.prefix {
			padding-inline-start: 3.5rem;
		}
	}

	.select {
		padding-inline-end: 3.5rem;
		text-overflow: ellipsis;
	}

	.textarea {
		padding-block: 0.75rem;
		resize: none;
	}

	.datalist::-webkit-calendar-picker-indicator {
		display: none !important;
	}

	.icon {
		inset: auto 1rem auto auto;
		pointer-events: none;
		position: absolute;
		size: 1.75rem;

		&.prefix {
			inset: auto auto auto 1rem;
		}

		&.alt {
			color: var(--c-charcoal);
			rotate: 180deg;
		}
	}

	.optional {
		font-size: 0.625em;
		font-weight: 400;
		margin-inline-start: 0.25em;
	}

	.instruction {
		margin-block-start: 0.625rem;
	}
</style>
